/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param */
import {
  INVOICE_COMPANY,
  INVOICE_FOOTER_TEXT,
  INVOICE_TAX,
  formatInvoiceNumber
} from '../config/invoice-config';

export type InvoicePayment = {
  id: number;
  amount: number;
  currency: string;
  paymentDate: string | null;
  paypalPaymentId: string;
  paypalPayerId: string;
  status: string;
  planName: string;
  subscriptionId: number;
};

export type InvoiceCustomer = {
  name: string;
  email: string;
};

function escapeHtml(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return String(v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return iso;
  }
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function buildInvoiceHtml(payment: InvoicePayment, customer: InvoiceCustomer): string {
  const paymentDate = payment.paymentDate ? new Date(payment.paymentDate) : new Date();
  const invoiceNumber = formatInvoiceNumber(payment.id, paymentDate);
  const subtotal = payment.amount;
  const tax = INVOICE_TAX ? (subtotal * INVOICE_TAX.ratePct) / 100 : 0;
  const total = subtotal + tax;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Invoice ${escapeHtml(invoiceNumber)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; color: #111; margin: 0; padding: 40px; }
    .invoice { max-width: 780px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 24px; }
    .from { font-size: 13px; line-height: 1.5; }
    .from .name { font-size: 18px; font-weight: 600; margin-bottom: 4px; }
    .meta { text-align: right; font-size: 13px; }
    .meta .title { font-size: 28px; font-weight: 700; letter-spacing: 2px; margin-bottom: 8px; }
    .meta .row { margin-bottom: 2px; }
    .meta .label { color: #666; margin-right: 6px; }
    .bill-to { margin-top: 32px; font-size: 13px; line-height: 1.5; }
    .bill-to .label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #666; margin-bottom: 4px; }
    .bill-to .name { font-weight: 600; }
    table.items { width: 100%; margin-top: 32px; border-collapse: collapse; font-size: 13px; }
    table.items th { text-align: left; border-bottom: 1px solid #111; padding: 10px 8px; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #666; }
    table.items td { padding: 12px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
    table.items td.num, table.items th.num { text-align: right; }
    .totals { margin-top: 16px; margin-left: auto; width: 50%; font-size: 13px; }
    .totals .row { display: flex; justify-content: space-between; padding: 6px 8px; }
    .totals .row.total { border-top: 2px solid #111; font-weight: 700; font-size: 16px; padding-top: 10px; margin-top: 4px; }
    .paid-stamp { display: inline-block; margin-top: 24px; padding: 8px 16px; border: 2px solid #0a7d3b; color: #0a7d3b; font-weight: 700; letter-spacing: 2px; transform: rotate(-4deg); }
    .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #eee; font-size: 12px; color: #666; line-height: 1.5; }
    .footer .ids { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; }
    @media print {
      body { padding: 24px; }
      .no-print { display: none !important; }
    }
    .print-btn { position: fixed; top: 16px; right: 16px; padding: 10px 16px; background: #111; color: #fff; border: none; border-radius: 4px; font-size: 14px; cursor: pointer; }
  </style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">Download / Print</button>
  <div class="invoice">
    <div class="header">
      <div class="from">
        <div class="name">${escapeHtml(INVOICE_COMPANY.name)}</div>
        <div>${escapeHtml(INVOICE_COMPANY.addressLine1)}</div>
        ${INVOICE_COMPANY.addressLine2 ? `<div>${escapeHtml(INVOICE_COMPANY.addressLine2)}</div>` : ''}
        <div>${escapeHtml(INVOICE_COMPANY.cityStateZip)}</div>
        <div>${escapeHtml(INVOICE_COMPANY.country)}</div>
        <div style="margin-top:6px;">${escapeHtml(INVOICE_COMPANY.email)} · ${escapeHtml(INVOICE_COMPANY.phone)}</div>
        <div>${escapeHtml(INVOICE_COMPANY.website)}</div>
        <div style="margin-top:6px; color:#666;">Tax ID: ${escapeHtml(INVOICE_COMPANY.taxId)}</div>
      </div>
      <div class="meta">
        <div class="title">INVOICE</div>
        <div class="row"><span class="label">Invoice #</span>${escapeHtml(invoiceNumber)}</div>
        <div class="row"><span class="label">Issue Date</span>${escapeHtml(formatDate(payment.paymentDate))}</div>
        <div class="row"><span class="label">Status</span>${escapeHtml((payment.status || '').toUpperCase())}</div>
      </div>
    </div>
    <div class="bill-to">
      <div class="label">Bill To</div>
      <div class="name">${escapeHtml(customer.name || customer.email)}</div>
      <div>${escapeHtml(customer.email)}</div>
    </div>
    <table class="items">
      <thead>
        <tr>
          <th>Description</th>
          <th class="num">Qty</th>
          <th class="num">Unit Price</th>
          <th class="num">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <div><strong>${escapeHtml(payment.planName)} Plan</strong> — Monthly subscription</div>
            <div style="color:#666; font-size:12px; margin-top:2px;">Subscription ID: ${escapeHtml(payment.subscriptionId)}</div>
          </td>
          <td class="num">1</td>
          <td class="num">${escapeHtml(formatMoney(subtotal, payment.currency))}</td>
          <td class="num">${escapeHtml(formatMoney(subtotal, payment.currency))}</td>
        </tr>
      </tbody>
    </table>
    <div class="totals">
      <div class="row"><span>Subtotal</span><span>${escapeHtml(formatMoney(subtotal, payment.currency))}</span></div>
      ${INVOICE_TAX ? `<div class="row"><span>${escapeHtml(INVOICE_TAX.label)} (${escapeHtml(INVOICE_TAX.ratePct)}%)</span><span>${escapeHtml(formatMoney(tax, payment.currency))}</span></div>` : ''}
      <div class="row total"><span>Total</span><span>${escapeHtml(formatMoney(total, payment.currency))}</span></div>
    </div>
    ${(payment.status || '').toLowerCase() === 'approved' ? '<div class="paid-stamp">PAID</div>' : ''}
    <div class="footer">
      <div>${escapeHtml(INVOICE_FOOTER_TEXT)}</div>
      <div class="ids" style="margin-top:8px;">PayPal Payment: ${escapeHtml(payment.paypalPaymentId)}</div>
      <div class="ids">PayPal Payer: ${escapeHtml(payment.paypalPayerId)}</div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Open the invoice in a new window and immediately trigger the print dialog
 * so the user can save as PDF.
 */
export function downloadInvoice(payment: InvoicePayment, customer: InvoiceCustomer): void {
  const html = buildInvoiceHtml(payment, customer);
  const win = window.open('', '_blank');
  if (!win) {
    // Popup blocked — fall back to a data URL download so the user can still get the invoice.
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formatInvoiceNumber(
      payment.id,
      payment.paymentDate ? new Date(payment.paymentDate) : new Date()
    )}.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  // Give the new window a moment to lay out before opening the print dialog.
  win.onload = () => {
    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch {
        /* user can still click the Download/Print button */
      }
    }, 200);
  };
}
